import React, { FC, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { message } from 'antd';
import { FastAverageColor } from 'fast-average-color';
import { useAppDispatch, useAppSelector } from '../../store/store';
import { uiActions } from '../../store/slices/ui';
import { playerService } from '../../services/player';
import { msToTime } from '../../utils';
import { spotifyActions } from '../../store/slices/spotify';
import { AddSongToLibraryButton } from '../Actions/AddSongToLibrary';
import { Marquee } from '../Layout/components/PlayingBar/Marquee';
import { Tooltip } from '../Tooltip';
import { miniPlayerManager } from './miniPlayerManager';
import './index.scss';

/* --- SVG Icons --- */
const SlidersIcon = () => (
  <svg role='img' height='13' width='15' viewBox='0 0 15 13' fill='none'>
    <line x1='0' y1='3.5' x2='15' y2='3.5' stroke='currentColor' strokeWidth='1.5' strokeLinecap='round' />
    <circle cx='10.5' cy='3.5' r='2.2' fill='#121212' stroke='currentColor' strokeWidth='1.5' />
    <line x1='0' y1='9.5' x2='15' y2='9.5' stroke='currentColor' strokeWidth='1.5' strokeLinecap='round' />
    <circle cx='4.5' cy='9.5' r='2.2' fill='#121212' stroke='currentColor' strokeWidth='1.5' />
  </svg>
);

const DragDotsIcon = () => (
  <svg role='img' height='10' width='18' viewBox='0 0 18 10' fill='currentColor'>
    <circle cx='2.5' cy='2.5' r='1.3' />
    <circle cx='6.5' cy='2.5' r='1.3' />
    <circle cx='10.5' cy='2.5' r='1.3' />
    <circle cx='14.5' cy='2.5' r='1.3' />
    <circle cx='2.5' cy='7.5' r='1.3' />
    <circle cx='6.5' cy='7.5' r='1.3' />
    <circle cx='10.5' cy='7.5' r='1.3' />
    <circle cx='14.5' cy='7.5' r='1.3' />
  </svg>
);

const CloseIcon = () => (
  <svg role='img' height='12' width='12' viewBox='0 0 12 12' fill='none'>
    <path d='M1.5 1.5l9 9M10.5 1.5l-9 9' stroke='currentColor' strokeWidth='1.5' strokeLinecap='round' />
  </svg>
);

const VolumeIcon = ({ muted }: { muted: boolean }) => (
  <svg role='img' height='16' width='16' viewBox='0 0 16 16' fill='currentColor'>
    {muted ? (
      <path d='M13.86 5.47a.75.75 0 0 0-1.06 1.06l1.47 1.47-1.47 1.47a.75.75 0 1 0 1.06 1.06l1.47-1.47 1.47 1.47a.75.75 0 0 0 1.06-1.06L16.38 8l1.47-1.47a.75.75 0 0 0-1.06-1.06L15.32 6.94l-1.46-1.47zM9.741.05a.75.75 0 0 1 .375.65v14.6a.75.75 0 0 1-1.255.555L4.417 11.5H1.75A1.75 1.75 0 0 1 0 9.75v-3.5C0 5.284.784 4.5 1.75 4.5h2.667L8.86.605a.75.75 0 0 1 .881-.555z' />
    ) : (
      <path d='M9.741.05a.75.75 0 0 1 .375.65v14.6a.75.75 0 0 1-1.255.555L4.417 11.5H1.75A1.75 1.75 0 0 1 0 9.75v-3.5C0 5.284.784 4.5 1.75 4.5h2.667L8.86.605a.75.75 0 0 1 .881-.555zm-1.24 2.198L4.693 5.5H1.75a.25.25 0 0 0-.25.25v3.5c0 .138.112.25.25.25h2.943l3.808 3.252V2.248zM11.5 4.475a.75.75 0 0 1 1.06 0A5.972 5.972 0 0 1 14.3 8a5.972 5.972 0 0 1-1.74 3.525.75.75 0 0 1-1.06-1.06A4.472 4.472 0 0 0 12.8 8c0-1.243-.503-2.368-1.3-3.175a.75.75 0 0 1 0-1.06z' />
    )}
  </svg>
);

const ShuffleIcon = ({ active }: { active: boolean }) => (
  <svg role='img' height='16' width='16' viewBox='0 0 16 16' fill={active ? '#1ed760' : 'currentColor'}>
    <path d='M13.151.922a.75.75 0 1 0-1.06 1.06L13.109 3H11.16a3.75 3.75 0 0 0-2.873 1.34l-6.173 7.356A2.25 2.25 0 0 1 .39 12.5H0V14h.391a3.75 3.75 0 0 0 2.873-1.34l6.173-7.356a2.25 2.25 0 0 1 1.724-.804h1.947l-1.017 1.018a.75.75 0 0 0 1.06 1.06L15.98 3.75 13.15.922zM.391 3.5H0V2h.391c.683 0 1.329.314 1.753.854l2.122 2.702-1.18 1.407L1.085 4.354A2.25 2.25 0 0 0 .391 3.5zm10.77 8.5H13.11l-1.018 1.018a.75.75 0 0 0 1.06 1.06L15.98 11.25 13.15 8.422a.75.75 0 0 0-1.06 1.06L13.109 10.5h-1.947a2.25 2.25 0 0 1-1.724-.804l-2.122-2.702 1.18-1.407 2.029 2.583c.424.54 1.07.854 1.753.854z' />
  </svg>
);

const PrevIcon = () => (
  <svg role='img' height='18' width='18' viewBox='0 0 16 16' fill='currentColor'>
    <path d='M3.3 1a.7.7 0 0 1 .7.7v5.15l9.95-5.744a.7.7 0 0 1 1.05.606v12.576a.7.7 0 0 1-1.05.607L4 9.149V14.3a.7.7 0 0 1-.7.7H1.7a.7.7 0 0 1-.7-.7V1.7a.7.7 0 0 1 .7-.7h1.6z' />
  </svg>
);

const PlayIcon = () => (
  <svg role='img' height='18' width='18' viewBox='0 0 24 24' fill='currentColor' style={{ marginLeft: 2 }}>
    <path d='m7.05 3.606 13.49 7.788a.7.7 0 0 1 0 1.212L7.05 20.394A.7.7 0 0 1 6 19.788V4.212a.7.7 0 0 1 1.05-.606z' />
  </svg>
);

const PauseIcon = () => (
  <svg role='img' height='18' width='18' viewBox='0 0 24 24' fill='currentColor'>
    <path d='M5.7 3a.7.7 0 0 0-.7.7v16.6a.7.7 0 0 0 .7.7h2.6a.7.7 0 0 0 .7-.7V3.7a.7.7 0 0 0-.7-.7H5.7zm10 0a.7.7 0 0 0-.7.7v16.6a.7.7 0 0 0 .7.7h2.6a.7.7 0 0 0 .7-.7V3.7a.7.7 0 0 0-.7-.7h-2.6z' />
  </svg>
);

const NextIcon = () => (
  <svg role='img' height='18' width='18' viewBox='0 0 16 16' fill='currentColor'>
    <path d='M12.7 1a.7.7 0 0 0-.7.7v5.15L2.05 1.107A.7.7 0 0 0 1 1.712v12.576a.7.7 0 0 0 1.05.607L12 9.149V14.3a.7.7 0 0 0 .7.7h1.6a.7.7 0 0 0 .7-.7V1.7a.7.7 0 0 0-.7-.7h-1.6z' />
  </svg>
);

const RepeatIcon = ({ mode }: { mode: number }) => (
  <svg role='img' height='16' width='16' viewBox='0 0 16 16' fill={mode > 0 ? '#1ed760' : 'currentColor'}>
    <path d='M0 4.75A3.75 3.75 0 0 1 3.75 1h8.5A3.75 3.75 0 0 1 16 4.75v5a3.75 3.75 0 0 1-3.75 3.75H9.81l1.018 1.018a.75.75 0 1 1-1.06 1.06L6.939 12.75l2.829-2.828a.75.75 0 1 1 1.06 1.06L9.811 12h2.439a2.25 2.25 0 0 0 2.25-2.25v-5a2.25 2.25 0 0 0-2.25-2.25h-8.5A2.25 2.25 0 0 0 1.5 4.75v5A2.25 2.25 0 0 0 3.75 12H5v1.5H3.75A3.75 3.75 0 0 1 0 9.75v-5z' />
  </svg>
);

const ShareIcon = () => (
  <svg role='img' height='16' width='16' viewBox='0 0 16 16' fill='currentColor'>
    <path d='M13.5 11.5v2a.5.5 0 0 1-.5.5H3a.5.5 0 0 1-.5-.5v-2H1v2a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-2h-1.5zM8 1.5a.75.75 0 0 1 .53.22l3.5 3.5a.75.75 0 0 1-1.06 1.06L9 4.31v7.44a.75.75 0 0 1-1.5 0V4.31L5.53 6.28a.75.75 0 0 1-1.06-1.06l3.5-3.5A.75.75 0 0 1 8 1.5z' />
  </svg>
);

const HideIcon = () => (
  <svg role='img' height='16' width='16' viewBox='0 0 16 16' fill='currentColor'>
    <path d='M2.47 2.47a.75.75 0 0 1 1.06 0L8 6.94l4.47-4.47a.75.75 0 1 1 1.06 1.06L9.06 8l4.47 4.47a.75.75 0 1 1-1.06 1.06L8 9.06l-4.47 4.47a.75.75 0 0 1-1.06-1.06L6.94 8 2.47 3.53a.75.75 0 0 1 0-1.06Z' />
  </svg>
);

interface MiniPlayerContentProps {
  isPip: boolean;
  onClose: () => void;
  width?: number;
  height?: number;
  pos?: { x: number; y: number };
  onDragMouseDown?: (e: React.MouseEvent) => void;
  onResizeMouseDown?: (e: React.MouseEvent) => void;
}

export const MiniPlayerContent: FC<MiniPlayerContentProps> = memo(
  ({ isPip, onClose, width, height, pos, onDragMouseDown, onResizeMouseDown }) => {
    const dispatch = useAppDispatch();
    const state = useAppSelector((s) => s.spotify.state);
    const currentTrack = state?.track_window?.current_track;
    const isPaused = state?.paused ?? true;
    const repeatMode = state?.repeat_mode ?? 0;
    const isShuffle = !!state?.shuffle;
    const durationMs = state?.duration || currentTrack?.duration_ms || 0;
    const liked = useAppSelector((s) => s.spotify.liked);

    // Live position & seek scrubbing
    const [livePositionMs, setLivePositionMs] = useState(0);
    const [isScrubbing, setIsScrubbing] = useState(false);
    const [isMuted, setIsMuted] = useState(false);
    const previousVolumeRef = useRef(1);

    // Dynamic background color extracted from album cover
    const [cardBg, setCardBg] = useState<string>('#3b1912');

    // 10-second activity hover timer for top bar & overlay controls
    const [isUserActive, setIsUserActive] = useState(true);
    const activityTimerRef = useRef<NodeJS.Timeout | null>(null);

    const handleUserActivity = useCallback(() => {
      setIsUserActive(true);
      if (activityTimerRef.current) {
        clearTimeout(activityTimerRef.current);
      }
      activityTimerRef.current = setTimeout(() => {
        setIsUserActive(false);
      }, 10000); // 10s as requested
    }, []);

    useEffect(() => {
      // Start 10s timer upon mount
      activityTimerRef.current = setTimeout(() => {
        setIsUserActive(false);
      }, 10000);

      return () => {
        if (activityTimerRef.current) {
          clearTimeout(activityTimerRef.current);
        }
      };
    }, []);

    // Window dimensions for responsive mode switching
    const [winSize, setWinSize] = useState({
      width: width || 320,
      height: height || 380,
    });

    useEffect(() => {
      const targetWin = isPip && miniPlayerManager.pipWindow ? miniPlayerManager.pipWindow : window;

      const handleResize = () => {
        if (isPip && miniPlayerManager.pipWindow) {
          setWinSize({
            width: miniPlayerManager.pipWindow.innerWidth,
            height: miniPlayerManager.pipWindow.innerHeight,
          });
        } else if (width && height) {
          setWinSize({ width, height });
        }
      };

      handleResize();
      targetWin.addEventListener('resize', handleResize);
      return () => targetWin.removeEventListener('resize', handleResize);
    }, [isPip, width, height]);

    // Determine current layout mode based on dimensions
    const mode: 'card' | 'compact' | 'slim' = useMemo(() => {
      const h = winSize.height;
      const w = winSize.width;
      if (h < 125) return 'slim';
      if (h < 240 || (w >= 330 && h < 280)) return 'compact';
      return 'card';
    }, [winSize.width, winSize.height]);

    // Sync position from playback state
    useEffect(() => {
      if (!isScrubbing && state?.position !== undefined) {
        setLivePositionMs(state.position);
      }
    }, [state?.position, isScrubbing]);

    // Keep position ticking while playing
    useEffect(() => {
      if (isPaused || isScrubbing) return;
      const interval = setInterval(() => {
        setLivePositionMs((prev) => Math.min(prev + 250, durationMs));
      }, 250);
      return () => clearInterval(interval);
    }, [isPaused, isScrubbing, durationMs]);

    const imageUrl =
      currentTrack?.album?.images?.[0]?.url ||
      (currentTrack as any)?.image?.[0]?.url ||
      '';

    // Extract dominant color
    useEffect(() => {
      if (imageUrl) {
        const fac = new FastAverageColor();
        fac
          .getColorAsync(imageUrl)
          .then((color) => {
            setCardBg(color.hex);
          })
          .catch(() => {
            setCardBg('#3b1912');
          });
      }
    }, [imageUrl]);

    // Playback Handlers
    const handleTogglePlay = useCallback(() => {
      handleUserActivity();
      if (isPaused) {
        playerService.startPlayback();
      } else {
        playerService.pausePlayback();
      }
    }, [isPaused, handleUserActivity]);

    const handleNext = useCallback(() => {
      handleUserActivity();
      playerService.nextTrack();
    }, [handleUserActivity]);

    const handlePrev = useCallback(() => {
      handleUserActivity();
      playerService.previousTrack();
    }, [handleUserActivity]);

    const handleToggleMute = useCallback(() => {
      handleUserActivity();
      if (isMuted) {
        setIsMuted(false);
        playerService.setVolume(Math.round(previousVolumeRef.current * 100));
      } else {
        setIsMuted(true);
        playerService.setVolume(0);
      }
    }, [isMuted, handleUserActivity]);

    const handleToggleRepeat = useCallback(() => {
      handleUserActivity();
      if (repeatMode === 0) playerService.setRepeatMode('context');
      else if (repeatMode === 1) playerService.setRepeatMode('track');
      else playerService.setRepeatMode('off');
    }, [repeatMode, handleUserActivity]);

    const handleToggleShuffle = useCallback(() => {
      handleUserActivity();
      playerService.toggleShuffle(!isShuffle);
    }, [isShuffle, handleUserActivity]);

    const handleCopyLink = useCallback(() => {
      handleUserActivity();
      if (currentTrack?.id) {
        const trackUrl = `${window.location.origin}/track/${currentTrack.id}`;
        navigator.clipboard.writeText(trackUrl);
        message.success('Link copied to clipboard');
      }
    }, [currentTrack?.id, handleUserActivity]);

    // Seekbar scrubbing
    const handleSeekMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      handleUserActivity();
      setIsScrubbing(true);
      const rect = e.currentTarget.getBoundingClientRect();
      const calculateTime = (clientX: number) => {
        const clickX = Math.max(0, Math.min(rect.width, clientX - rect.left));
        const percent = clickX / rect.width;
        return Math.round(percent * durationMs);
      };
      const targetMs = calculateTime(e.clientX);
      setLivePositionMs(targetMs);

      const targetWindow = isPip && miniPlayerManager.pipWindow ? miniPlayerManager.pipWindow : window;

      const onMouseMove = (moveEvent: MouseEvent) => {
        const ms = calculateTime(moveEvent.clientX);
        setLivePositionMs(ms);
      };

      const onMouseUp = (upEvent: MouseEvent) => {
        targetWindow.removeEventListener('mousemove', onMouseMove);
        targetWindow.removeEventListener('mouseup', onMouseUp);
        setIsScrubbing(false);
        const finalMs = calculateTime(upEvent.clientX);
        playerService.seekToPosition(finalMs);
      };

      targetWindow.addEventListener('mousemove', onMouseMove);
      targetWindow.addEventListener('mouseup', onMouseUp);
    };

    if (!currentTrack) return null;

    const artistsText = currentTrack.artists?.map((a: any) => a.name).join(', ') || '';
    const currentProgress = durationMs > 0 ? (livePositionMs / durationMs) * 100 : 0;

    return (
      <div
        className={`spotify-miniplayer mode-${mode} ${isPip ? 'pip-mode' : 'in-page-mode'} ${
          isUserActive ? 'user-active' : 'user-idle'
        }`}
        style={
          !isPip
            ? {
                left: pos?.x ?? 100,
                top: pos?.y ?? 100,
                width: width ?? 320,
                height: height ?? 380,
              }
            : undefined
        }
        onMouseMove={handleUserActivity}
        onMouseEnter={handleUserActivity}
        onClick={handleUserActivity}
      >
        {/* TOP BAR (Sliders, 8 dots, Close) - Shows on hover, stays 10s */}
        {mode !== 'slim' && (
          <div
            className={`spotify-miniplayer-header ${isUserActive ? 'visible' : 'hidden'}`}
            onMouseDown={!isPip ? onDragMouseDown : undefined}
          >
            <button
              className='miniplayer-header-btn'
              title='Options'
              onClick={() => {
                dispatch(uiActions.openRightLayout());
                window.focus();
              }}
            >
              <SlidersIcon />
            </button>

            <div className='miniplayer-drag-handle' title={isPip ? 'OpenMusic Miniplayer' : 'Drag'}>
              <DragDotsIcon />
            </div>

            <button className='miniplayer-header-btn' title='Close' onClick={onClose}>
              <CloseIcon />
            </button>
          </div>
        )}

        {/* 1. CARD VIEW (Default large format) */}
        {mode === 'card' && (
          <div className='spotify-miniplayer-card-container'>
            {/* Colored Inner Card */}
            <div
              className='spotify-miniplayer-inner-card'
              style={{
                backgroundColor: cardBg,
              }}
            >
              {/* Album Artwork */}
              <div className='spotify-miniplayer-card-art-box'>
                <img src={imageUrl} alt={currentTrack.name} className='miniplayer-art-img' />

                {/* Overlay Playback Controls (Fades in when active) */}
                <div className={`miniplayer-card-overlay ${isUserActive ? 'active' : ''}`}>
                  <button
                    className='miniplayer-icon-btn'
                    onClick={handleToggleMute}
                    title={isMuted ? 'Unmute' : 'Mute'}
                  >
                    <VolumeIcon muted={isMuted} />
                  </button>

                  <button className='miniplayer-icon-btn' onClick={handlePrev} title='Previous'>
                    <PrevIcon />
                  </button>

                  <button
                    className='miniplayer-icon-btn play-circle-btn'
                    onClick={handleTogglePlay}
                    title={isPaused ? 'Play' : 'Pause'}
                  >
                    {isPaused ? <PlayIcon /> : <PauseIcon />}
                  </button>

                  <button className='miniplayer-icon-btn' onClick={handleNext} title='Next'>
                    <NextIcon />
                  </button>

                  <button
                    className='miniplayer-icon-btn'
                    onClick={handleToggleRepeat}
                    title='Repeat'
                    style={{ color: repeatMode > 0 ? '#1ed760' : undefined }}
                  >
                    <RepeatIcon mode={repeatMode} />
                  </button>

                  <Tooltip title='Copy link to Song'>
                    <button className='miniplayer-icon-btn' onClick={handleCopyLink}>
                      <ShareIcon />
                    </button>
                  </Tooltip>
                </div>
              </div>

              {/* Seekbar inside Card (Fades in when active) */}
              <div className={`miniplayer-card-seekbar-wrap ${isUserActive ? 'active' : ''}`}>
                <div className='miniplayer-time-row'>
                  <span className='time-current'>{msToTime(livePositionMs)}</span>
                  <span className='time-total'>{msToTime(durationMs)}</span>
                </div>
                <div className='miniplayer-progress-bar' onMouseDown={handleSeekMouseDown}>
                  <div
                    className='miniplayer-progress-fill'
                    style={{ width: `${Math.min(100, Math.max(0, currentProgress))}%` }}
                  >
                    <div className='miniplayer-progress-handle' />
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom Info Row */}
            <div className='spotify-miniplayer-footer'>
              <div className='miniplayer-meta-box'>
                <Marquee className='miniplayer-track-title' title={currentTrack.name}>
                  {currentTrack.name}
                </Marquee>
                <div className='miniplayer-track-artist' title={artistsText}>
                  {artistsText}
                </div>
              </div>

              <div className='miniplayer-actions-box'>
                <button
                  className='miniplayer-hide-btn'
                  title="Don't play this song"
                  onClick={() => playerService.nextTrack()}
                >
                  <HideIcon />
                </button>

                <AddSongToLibraryButton
                  size={20}
                  id={currentTrack.id || ''}
                  isSaved={!!liked}
                  onToggle={() => dispatch(spotifyActions.setLiked({ liked: !liked }))}
                />
              </div>
            </div>
          </div>
        )}

        {/* 2. COMPACT VIEW (Horizontal 2-row format) */}
        {mode === 'compact' && (
          <div className='spotify-miniplayer-compact-container'>
            {/* Top Row: Thumbnail, Track/Artist, Hide, Like */}
            <div className='compact-top-row'>
              <img src={imageUrl} alt={currentTrack.name} className='compact-thumb-img' />

              <div className='compact-meta-box'>
                <Marquee className='miniplayer-track-title' title={currentTrack.name}>
                  {currentTrack.name}
                </Marquee>
                <div className='miniplayer-track-artist' title={artistsText}>
                  {artistsText}
                </div>
              </div>

              <div className='compact-actions-box'>
                <button
                  className='miniplayer-hide-btn'
                  title="Don't play this song"
                  onClick={() => playerService.nextTrack()}
                >
                  <HideIcon />
                </button>

                <AddSongToLibraryButton
                  size={20}
                  id={currentTrack.id || ''}
                  isSaved={!!liked}
                  onToggle={() => dispatch(spotifyActions.setLiked({ liked: !liked }))}
                />
              </div>
            </div>

            {/* Bottom Row: Full horizontal controls */}
            <div className='compact-bottom-row'>
              <button
                className='miniplayer-icon-btn'
                onClick={handleToggleMute}
                title={isMuted ? 'Unmute' : 'Mute'}
              >
                <VolumeIcon muted={isMuted} />
              </button>

              <button
                className='miniplayer-icon-btn'
                onClick={handleToggleShuffle}
                title='Shuffle'
                style={{ color: isShuffle ? '#1ed760' : undefined }}
              >
                <ShuffleIcon active={isShuffle} />
              </button>

              <button className='miniplayer-icon-btn' onClick={handlePrev} title='Previous'>
                <PrevIcon />
              </button>

              <button
                className='miniplayer-icon-btn play-circle-btn compact-play'
                onClick={handleTogglePlay}
                title={isPaused ? 'Play' : 'Pause'}
              >
                {isPaused ? <PlayIcon /> : <PauseIcon />}
              </button>

              <button className='miniplayer-icon-btn' onClick={handleNext} title='Next'>
                <NextIcon />
              </button>

              <button
                className='miniplayer-icon-btn'
                onClick={handleToggleRepeat}
                title='Repeat'
                style={{ color: repeatMode > 0 ? '#1ed760' : undefined }}
              >
                <RepeatIcon mode={repeatMode} />
              </button>

              <Tooltip title='Copy link to Song'>
                <button className='miniplayer-icon-btn' onClick={handleCopyLink}>
                  <ShareIcon />
                </button>
              </Tooltip>
            </div>
          </div>
        )}

        {/* 3. SLIM STRIP VIEW (Ultra-compact single line) */}
        {mode === 'slim' && (
          <div className='spotify-miniplayer-slim-container'>
            <img src={imageUrl} alt={currentTrack.name} className='slim-thumb-img' />

            <div className='slim-meta-box'>
              <Marquee className='miniplayer-track-title' title={currentTrack.name}>
                {currentTrack.name}
              </Marquee>
              <div className='miniplayer-track-artist' title={artistsText}>
                {artistsText}
              </div>
            </div>

            <div className='slim-actions-box'>
              <AddSongToLibraryButton
                size={18}
                id={currentTrack.id || ''}
                isSaved={!!liked}
                onToggle={() => dispatch(spotifyActions.setLiked({ liked: !liked }))}
              />

              <button
                className='miniplayer-icon-btn play-circle-btn slim-play'
                onClick={handleTogglePlay}
                title={isPaused ? 'Play' : 'Pause'}
              >
                {isPaused ? <PlayIcon /> : <PauseIcon />}
              </button>

              <button className='miniplayer-icon-btn' onClick={handleNext} title='Next'>
                <NextIcon />
              </button>
            </div>
          </div>
        )}

        {/* Corner resize grip (only in in-page fallback mode) */}
        {!isPip && onResizeMouseDown && (
          <div
            className='spotify-miniplayer-resize-handle'
            onMouseDown={onResizeMouseDown}
            title='Resize'
          >
            <svg width='10' height='10' viewBox='0 0 10 10' fill='none' stroke='currentColor' strokeWidth='1.5'>
              <line x1='9' y1='1' x2='1' y2='9' />
              <line x1='9' y1='4.5' x2='4.5' y2='9' />
              <line x1='9' y1='8' x2='8' y2='9' />
            </svg>
          </div>
        )}
      </div>
    );
  }
);

MiniPlayerContent.displayName = 'MiniPlayerContent';

export const MiniPlayer: FC = memo(() => {
  const dispatch = useAppDispatch();
  const isOpen = useAppSelector((state) => state.ui.miniplayerOpen);
  const currentTrack = useAppSelector((state) => state.spotify.state?.track_window?.current_track);
  const [pipWindow, setPipWindow] = useState<Window | null>(() => miniPlayerManager.pipWindow);

  useEffect(() => {
    return miniPlayerManager.subscribe((win) => {
      setPipWindow(win);
    });
  }, []);

  const handleClose = useCallback(() => {
    miniPlayerManager.close();
    dispatch(uiActions.closeMiniplayer());
  }, [dispatch]);

  if (!isOpen || !currentTrack) return null;

  // Render exclusively into the separate native OS window
  if (pipWindow && !pipWindow.closed) {
    const mountNode =
      pipWindow.document.getElementById('miniplayer-portal-root') ||
      pipWindow.document.body;

    return ReactDOM.createPortal(
      <MiniPlayerContent isPip={true} onClose={handleClose} />,
      mountNode
    );
  }

  return null;
});

MiniPlayer.displayName = 'MiniPlayer';
export default MiniPlayer;
